import React, { useState, useEffect, useCallback, useMemo } from 'react';
import RmsRevenueDashboard from './RmsRevenueDashboard';

// Revenue Manager / RMS (Phase 3 / M1) — the decide-and-apply surface.
//   Decisions: ingest accepted Price Intelligence recommendations, clamp by guardrails,
//     and (per mode) manually apply → write the PMS rate calendar + push the Channel
//     Manager, with one-click rollback. Every apply/rollback is audited.
//   Automation: mode (OFF/ADVISORY/APPROVAL) + horizon + guardrails. AUTOMATIC is M2.
//   Audit: every PMS/CM push.
// Applying changes LIVE rates — mode + guardrails are always visible; apply is manual.
// Spec: docs/DEV_REVENUEMANAGER_PHASE3.md §7 (M1).

const fmtINR = (n) => (n == null ? '—' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`);
const MODE_META = {
  OFF:       { bg: '#fef2f2', fg: '#b91c1c', edge: '#fecaca', note: 'No decisions are generated. Set a mode to begin.' },
  ADVISORY:  { bg: '#e0f2fe', fg: '#0369a1', edge: '#7dd3fc', note: 'Recommends only — you apply each decision manually.' },
  APPROVAL:  { bg: '#fef3c7', fg: '#92400e', edge: '#fcd34d', note: 'Decisions queue for one-click approve → apply + push.' },
  AUTOMATIC: { bg: '#d1fae5', fg: '#047857', edge: '#6ee7b7', note: 'Auto-applies within guardrails (ships in M2).' },
};
const STATUS_CHIP = {
  PROPOSED:    { bg: '#fef3c7', fg: '#92400e', label: 'Proposed' },
  APPROVED:    { bg: '#dbeafe', fg: '#1e40af', label: 'Approved' },
  APPLIED:     { bg: '#d1fae5', fg: '#047857', label: 'Applied' },
  ROLLED_BACK: { bg: '#f1f5f9', fg: '#475569', label: 'Rolled back' },
  REJECTED:    { bg: '#fee2e2', fg: '#b91c1c', label: 'Rejected' },
  FAILED:      { bg: '#fee2e2', fg: '#b91c1c', label: 'Failed' },
  HELD:        { bg: '#ede9fe', fg: '#6d28d9', label: 'Held' },
};

const btn = (kind) => ({
  padding: '7px 13px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  border: ['primary', 'accept', 'reject', 'apply'].includes(kind) ? 'none' : '1px solid var(--theme-border, #e5e7eb)',
  background: kind === 'primary' ? 'var(--theme-primary, #0d8f83)' : kind === 'accept' ? '#059669' : kind === 'reject' ? '#dc2626' : kind === 'apply' ? '#7c3aed' : 'var(--theme-surface, #fff)',
  color: ['primary', 'accept', 'reject', 'apply'].includes(kind) ? '#fff' : 'var(--theme-text, #374151)',
});
const sel = { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--theme-border, #e5e7eb)', background: 'var(--theme-surface, #fff)', color: 'var(--theme-text, #374151)', fontSize: 13 };
const banner = (kind) => ({
  padding: '9px 12px', borderRadius: 8, fontSize: 13, margin: '10px 0',
  background: kind === 'danger' ? '#fef2f2' : '#ecfeff', border: `1px solid ${kind === 'danger' ? '#fecaca' : '#a5f3fc'}`,
  color: kind === 'danger' ? '#b91c1c' : '#155e75',
});
const kpiBox = { flex: '1 1 130px', minWidth: 130, background: 'var(--theme-surface, #fff)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 12, padding: '12px 14px' };
const card = { background: 'var(--theme-surface, #fff)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 12, padding: 16 };
const th = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', padding: '8px 10px', borderBottom: '1px solid var(--theme-border, #e5e7eb)', position: 'sticky', top: 0, background: 'var(--theme-surface, #fff)' };
const td = { padding: '8px 10px', borderBottom: '1px solid var(--theme-border-light, #f1f5f9)', fontSize: 13 };

function Chip({ status }) { const c = STATUS_CHIP[status] || STATUS_CHIP.PROPOSED; return <span style={{ background: c.bg, color: c.fg, borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>{c.label}</span>; }

function isoToday(offset = 0) { return new Date(Date.now() + offset * 86400000).toISOString().slice(0, 10); }
const IMPACT_WINDOWS = {
  default: { label: 'Last 30 + next 60 days', from: () => isoToday(-30), to: () => isoToday(60) },
  trailing90: { label: 'Last 90 days (stayed)', from: () => isoToday(-90), to: () => isoToday(-1) },
  forward90: { label: 'Next 90 days (booking so far)', from: () => isoToday(0), to: () => isoToday(90) },
};
function toCsv(rows, columns) {
  const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const head = columns.map((c) => esc(c.label)).join(',');
  const body = (rows || []).map((r) => columns.map((c) => esc(typeof c.get === 'function' ? c.get(r) : r[c.key])).join(',')).join('\n');
  return `${head}\n${body}`;
}
function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function RevenueManagerRms({ token, selectedProperty, focus, onClearFocus, onSwitchScreen }) {
  const [tab, setTab] = useState('decisions');
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState('');
  const [meal, setMeal] = useState('EP');
  const [days, setDays] = useState(30);
  const [decisions, setDecisions] = useState([]);
  const [license, setLicense] = useState(null);
  const [config, setConfig] = useState(null);
  const [guard, setGuard] = useState(null);
  const [overrideRoom, setOverrideRoom] = useState(''); // '' = property default; else a room_type_id
  const [roomOverrides, setRoomOverrides] = useState([]); // rooms that already have a config/guardrail override
  const [audit, setAudit] = useState([]);
  const [impact, setImpact] = useState(null);
  const [impactWindow, setImpactWindow] = useState('default');
  const [impactLoading, setImpactLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [detail, setDetail] = useState(null);
  // Deep-link scoping from Rate Shopper/Price Intelligence (App.js's rmsFocus,
  // same lifted-state pattern as the Banquet lead-conflict focus). focusFilter
  // persists (scopes the date range + shows the banner) until the user clears
  // it; focusMatched tracks whether we've already attempted to open the match,
  // so re-loading decisions after an Approve/Apply doesn't keep re-opening it.
  const [focusFilter, setFocusFilter] = useState(null);
  const [focusMatched, setFocusMatched] = useState(null);

  const api = useMemo(() => async (path, opts = {}) => {
    const res = await fetch(`/api/v1/rms${path}`, {
      ...opts,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'x-chain-id': selectedProperty?.chain_id || '', 'x-property-id': selectedProperty?.id || '' },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, [token, selectedProperty]);

  useEffect(() => {
    if (!selectedProperty?.id) return;
    (async () => {
      try {
        const [r, l, c] = await Promise.all([api('/own-rooms'), api('/license'), api('/config')]);
        setRooms(r.rooms || []); setLicense(l); setConfig(c);
        if (r.rooms?.length) setRoom((prev) => prev || String(r.rooms[0].own_room_id));
      } catch (e) { setError(e.message); }
    })();
  }, [selectedProperty, api]);

  const loadDecisions = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const qs = focusFilter
        ? `from=${focusFilter.stay_date}&to=${focusFilter.stay_date}&meal=${meal}${room ? `&room=${room}` : ''}`
        : `days=${days}&meal=${meal}${room ? `&room=${room}` : ''}`;
      const d = await api(`/decisions?${qs}`);
      setDecisions(d.decisions || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [api, days, meal, room, selectedProperty, focusFilter]);

  // Consume a deep link from Rate Shopper/Price Intelligence: scope to that date/
  // room/meal and land on the Decisions tab.
  useEffect(() => {
    if (!focus) return;
    setTab('decisions');
    if (focus.room != null) setRoom(String(focus.room));
    if (focus.meal) setMeal(focus.meal);
    setFocusFilter({ stay_date: focus.stay_date, decisionId: focus.decisionId, room_name: focus.room_name });
    setFocusMatched(null);
    if (typeof onClearFocus === 'function') onClearFocus();
  }, [focus, onClearFocus]);

  // Once the scoped decisions arrive, open the matching one — or leave
  // focusMatched=false so the render below can show an honest reason instead of
  // a generic empty state. Runs once per focusFilter (guarded by focusMatched).
  useEffect(() => {
    if (!focusFilter || loading || focusMatched !== null) return;
    const match = focusFilter.decisionId
      ? decisions.find((d) => String(d.id) === String(focusFilter.decisionId))
      : decisions.find((d) => d.stay_date === focusFilter.stay_date);
    if (match) { setDetail(match); setFocusMatched(true); } else setFocusMatched(false);
  }, [decisions, loading, focusFilter, focusMatched]);

  const loadAutomation = useCallback(async () => {
    try {
      const roomQs = overrideRoom ? `?room=${overrideRoom}` : '';
      const [c, g, ro] = await Promise.all([api(`/config${roomQs}`), api(`/guardrails${roomQs}`), api('/room-overrides')]);
      setConfig(c); setGuard({ ...g, blackout_text: (g.blackout_dates || []).join(', ') }); setRoomOverrides(ro.overrides || []);
    } catch (e) { setError(e.message); }
  }, [api, overrideRoom]);
  const loadAudit = useCallback(async () => {
    try { const a = await api('/audit'); setAudit(a.log || []); } catch (e) { setError(e.message); }
  }, [api]);
  const loadImpact = useCallback(async () => {
    setImpactLoading(true); setError('');
    try {
      const w = IMPACT_WINDOWS[impactWindow] || IMPACT_WINDOWS.default;
      const qs = `from=${w.from()}&to=${w.to()}${room ? `&room=${room}` : ''}`;
      const r = await api(`/revenue-impact?${qs}`);
      setImpact(r);
    } catch (e) { setError(e.message); } finally { setImpactLoading(false); }
  }, [api, impactWindow, room]);

  useEffect(() => {
    if (tab === 'decisions') loadDecisions();
    else if (tab === 'automation') loadAutomation();
    else if (tab === 'audit') loadAudit();
    else if (tab === 'impact') loadImpact();
  }, [tab, loadDecisions, loadAutomation, loadAudit, loadImpact]);

  const generate = async () => {
    setBusy('generate'); setError(''); setMessage('');
    try {
      const r = await api('/decisions/generate', { method: 'POST', body: JSON.stringify({ days, meal }) });
      if (r.note) setMessage(r.note);
      else setMessage(`Generated ${r.written} decisions · ${r.clamped} clamped · ${r.held} held (blast radius) · ${r.skipped} skipped.`);
      await loadDecisions();
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  const act = async (id, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { const r = await api(`/decisions/${id}/${action}`, { method: 'POST' }); if (r.error) throw new Error(r.error); await loadDecisions(); if (detail?.id === id) setDetail(null); }
    catch (e) { setError(e.message); }
  };
  const applyAll = async () => {
    const n = decisions.filter((d) => d.status === 'PROPOSED' || d.status === 'APPROVED').length;
    if (!n || !window.confirm(`Apply ${n} decision(s)? This writes live rates to the PMS and pushes the Channel Manager.`)) return;
    setBusy('applyall');
    try { const r = await api('/decisions/apply-all', { method: 'POST', body: JSON.stringify({ meal, room: room || undefined }) }); setMessage(`Applied ${r.applied}${r.failed ? `, ${r.failed} failed` : ''}.`); await loadDecisions(); }
    catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  const saveConfig = async (mode) => {
    try {
      const r = await api('/config', { method: 'POST', body: JSON.stringify({ mode, horizon_days: config?.horizon_days || 30, room: overrideRoom || undefined }) });
      setConfig((c) => ({ ...c, mode: r.mode })); setMessage(`Mode set to ${r.mode}${overrideRoom ? ' for this room' : ''}.`);
      if (overrideRoom) await loadAutomation();
    } catch (e) { setError(e.message); }
  };
  const saveGuard = async () => {
    try {
      const blackout = (guard.blackout_text || '').split(',').map((s) => s.trim()).filter(Boolean);
      await api('/guardrails', { method: 'POST', body: JSON.stringify({ ...guard, blackout_dates: blackout, room: overrideRoom || undefined }) });
      setMessage(`Guardrails saved${overrideRoom ? ' for this room' : ''} — regenerate decisions to apply.`);
      if (overrideRoom) await loadAutomation();
    } catch (e) { setError(e.message); }
  };
  const clearOverride = async (roomId) => {
    if (!window.confirm(`Clear the mode + guardrail override for this room? It will go back to inheriting the property default.`)) return;
    try {
      await Promise.all([
        api(`/config?room=${roomId}`, { method: 'DELETE' }),
        api(`/guardrails?room=${roomId}`, { method: 'DELETE' }),
      ]);
      setMessage('Room override cleared.');
      await loadAutomation();
    } catch (e) { setError(e.message); }
  };

  const mode = config?.mode || 'OFF';
  const mm = MODE_META[mode] || MODE_META.OFF;

  const kpis = useMemo(() => {
    const by = (s) => decisions.filter((d) => d.status === s).length;
    const pend = decisions.filter((d) => d.status === 'PROPOSED' || d.status === 'APPROVED');
    const avgDelta = pend.length ? Math.round(pend.reduce((a, d) => a + (Number(d.delta_pct) || 0), 0) / pend.length) : null;
    return { proposed: by('PROPOSED') + by('APPROVED'), applied: by('APPLIED'), held: by('HELD'), failed: by('FAILED'), avgDelta };
  }, [decisions]);

  if (!selectedProperty?.id) return <div style={{ padding: 20 }}>Select a property to open Revenue Manager.</div>;

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Revenue Manager <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--theme-primary, #0d8f83)' }}>RMS</span></h2>
          <div style={{ color: 'var(--theme-text-muted)', fontSize: 13 }}>Decide &amp; apply prices — from accepted recommendations to live PMS + Channel Manager rates, guardrailed and reversible.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {tab === 'decisions' && <>
            <select value={room} onChange={(e) => setRoom(e.target.value)} style={sel} aria-label="Room type">{rooms.map((r) => <option key={r.own_room_id} value={r.own_room_id}>{r.name}</option>)}</select>
            <select value={meal} onChange={(e) => setMeal(e.target.value)} style={sel} aria-label="Meal plan">{['EP', 'CP', 'MAP', 'AP'].map((m) => <option key={m} value={m}>{m}</option>)}</select>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={sel} aria-label="Horizon">{[30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}</select>
            <button onClick={generate} style={btn('primary')} disabled={busy === 'generate' || mode === 'OFF'} title={mode === 'OFF' ? 'Set a mode first (Automation tab)' : ''}>{busy === 'generate' ? 'Generating…' : '⚙ Generate decisions'}</button>
          </>}
          {tab === 'impact' && <>
            <select value={room} onChange={(e) => setRoom(e.target.value)} style={sel} aria-label="Room type"><option value="">All rooms</option>{rooms.map((r) => <option key={r.own_room_id} value={r.own_room_id}>{r.name}</option>)}</select>
            <select value={impactWindow} onChange={(e) => setImpactWindow(e.target.value)} style={sel} aria-label="Window">{Object.entries(IMPACT_WINDOWS).map(([k, w]) => <option key={k} value={k}>{w.label}</option>)}</select>
            {impact?.rows?.length > 0 && (
              <button style={btn('ghost')} onClick={() => downloadCsv(`rms-revenue-impact-${impact.from}_${impact.to}.csv`, toCsv(impact.rows,
                [{ label: 'Date', key: 'stay_date' }, { label: 'Room', key: 'room_name' }, { label: 'Meal', key: 'meal_plan_code' }, { label: 'Prior rate', key: 'prior_rate' }, { label: 'New rate', key: 'new_rate' }, { label: 'Rooms booked since', key: 'rooms_booked' }, { label: 'Actual revenue', key: 'actual_revenue' }, { label: 'Counterfactual revenue', key: 'counterfactual_revenue' }, { label: 'Impact', key: 'impact' }]))}>⭳ CSV</button>
            )}
          </>}
        </div>
      </div>

      {/* Mode banner — always visible (safety) */}
      <div style={{ margin: '12px 0', padding: '10px 14px', borderRadius: 10, background: mm.bg, border: `1px solid ${mm.edge}`, color: mm.fg, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800, letterSpacing: '0.03em' }}>MODE: {mode}</span>
        <span style={{ fontSize: 13 }}>{mm.note}</span>
        {license && !license.price_intelligence_enabled && <span style={{ fontSize: 12, marginLeft: 'auto' }}>⚠ Price Intelligence not enabled — no accepted recommendations to ingest.</span>}
      </div>

      {/* Tabs */}
      <div role="tablist" style={{ display: 'flex', gap: 6, marginBottom: 12, borderBottom: '1px solid var(--theme-border, #e5e7eb)', paddingBottom: 8, flexWrap: 'wrap' }}>
        {[['dashboard', 'Dashboard'], ['decisions', 'Decisions'], ['impact', 'Revenue Impact'], ['automation', 'Automation'], ['audit', 'Audit']].map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} style={btn(tab === k ? 'primary' : 'ghost')}>{l}</button>
        ))}
      </div>

      {error && <div style={banner('danger')} role="alert">{error} <span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {message && <div style={banner('ok')}>{message} <span onClick={() => setMessage('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}

      {tab === 'decisions' && focusFilter && (
        <div style={{ ...banner('ok'), display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span>Showing RMS decisions for <strong>{focusFilter.stay_date}</strong>{focusFilter.room_name ? ` · ${focusFilter.room_name}` : ''} only.</span>
          <button onClick={() => { setFocusFilter(null); setFocusMatched(null); }} style={btn('ghost')}>Clear filter</button>
        </div>
      )}
      {tab === 'decisions' && focusFilter && focusMatched === false && mode === 'OFF' && (
        <div style={banner('danger')}>
          Revenue Manager mode is OFF for this property. Set a mode in the Automation tab, then come back and Generate decisions for {focusFilter.stay_date}.
          <button onClick={() => setTab('automation')} style={{ ...btn('ghost'), marginLeft: 10 }}>Go to Automation →</button>
        </div>
      )}
      {tab === 'decisions' && focusFilter && focusMatched === false && mode !== 'OFF' && (
        <div style={banner('danger')}>
          No RMS decision exists yet for {focusFilter.stay_date}{focusFilter.room_name ? ` / ${focusFilter.room_name}` : ''}. This is usually because Price Intelligence hasn't generated an ACCEPTED recommendation for this date yet.
          {typeof onSwitchScreen === 'function' && <button onClick={() => onSwitchScreen('price-intelligence')} style={{ ...btn('ghost'), marginLeft: 10 }}>Go to Price Intelligence →</button>}
        </div>
      )}

      {/* ── Decisions ── */}
      {tab === 'decisions' && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{kpis.proposed}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Awaiting apply</div></div>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{kpis.applied}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Applied</div></div>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{kpis.avgDelta != null ? `${kpis.avgDelta > 0 ? '+' : ''}${kpis.avgDelta}%` : '—'}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Avg Δ (pending)</div></div>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{kpis.held}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Held (blast radius)</div></div>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700, color: kpis.failed ? '#b91c1c' : undefined }}>{kpis.failed}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Failed</div></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <button onClick={applyAll} style={btn('apply')} disabled={!kpis.proposed || busy === 'applyall'}>▶ Apply all ({kpis.proposed})</button>
            <button onClick={() => setTab('automation')} style={btn('ghost')}>⚙ Mode & guardrails</button>
          </div>

          {loading ? <div style={{ padding: 24, color: 'var(--theme-text-muted)' }}>Loading…</div>
            : decisions.length === 0 ? (
              <div style={{ padding: 24, background: 'var(--theme-surface-2, #f1f5f9)', borderRadius: 10, color: 'var(--theme-text-muted)', textAlign: 'center' }}>
                No decisions yet. {mode === 'OFF' ? 'Set a mode in the Automation tab, then ' : ''}accept recommendations in Price Intelligence, then click <strong>⚙ Generate decisions</strong>.
              </div>
            ) : (
              <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead><tr>{['Date', 'Room · Meal', 'Current', 'Apply', 'Δ', 'Status', 'Guardrail', 'Actions'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {decisions.map((d) => {
                      const up = Number(d.delta_pct) > 0; const bound = d.guardrail_trace_json?.guardrail?.bound;
                      return (
                        <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => setDetail(d)}>
                          <td style={td}>{d.stay_date}</td>
                          <td style={td}>{d.room_name || d.room_type_id} · {d.meal_plan_code}</td>
                          <td style={{ ...td, color: 'var(--theme-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{fmtINR(d.prior_rate)}</td>
                          <td style={{ ...td, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtINR(d.clamped_rate)}</td>
                          <td style={{ ...td, color: up ? '#047857' : Number(d.delta_pct) < 0 ? '#b91c1c' : '#64748b' }}>{d.delta_pct != null ? `${up ? '+' : ''}${Number(d.delta_pct).toFixed(1)}%` : '—'}</td>
                          <td style={td}><Chip status={d.status} /></td>
                          <td style={{ ...td }}>{bound ? <span title={bound} style={{ color: '#d97706', fontSize: 12 }}>⚑ {bound.replace(/_/g, ' ').toLowerCase()}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                            {(d.status === 'PROPOSED' || d.status === 'HELD') && <button onClick={() => act(d.id, 'approve')} style={{ ...btn('ghost'), padding: '4px 8px', marginRight: 4 }}>Approve</button>}
                            {(d.status === 'PROPOSED' || d.status === 'APPROVED') && <button onClick={() => act(d.id, 'apply', `Apply ${fmtINR(d.clamped_rate)} for ${d.stay_date}? Writes live PMS + CM rate.`)} style={{ ...btn('apply'), padding: '4px 8px', marginRight: 4 }}>Apply</button>}
                            {(d.status === 'PROPOSED' || d.status === 'APPROVED' || d.status === 'HELD') && <button onClick={() => act(d.id, 'reject')} style={{ ...btn('reject'), padding: '4px 8px' }}>Reject</button>}
                            {d.status === 'APPLIED' && <button onClick={() => act(d.id, 'rollback', `Roll back to ${fmtINR(d.prior_rate)} for ${d.stay_date}?`)} style={{ ...btn('ghost'), padding: '4px 8px' }}>↩ Rollback</button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        </>
      )}

      {/* ── Dashboard ── */}
      {tab === 'dashboard' && <RmsRevenueDashboard token={token} selectedProperty={selectedProperty} />}

      {/* ── Revenue Impact ── */}
      {tab === 'impact' && (
        <>
          {impactLoading ? <div style={{ padding: 24, color: 'var(--theme-text-muted)' }}>Loading…</div> : impact && (
            <>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                <div style={kpiBox}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: impact.kpis.total_impact > 0 ? '#047857' : impact.kpis.total_impact < 0 ? '#b91c1c' : undefined }}>{impact.kpis.total_impact > 0 ? '+' : ''}{fmtINR(impact.kpis.total_impact)}</div>
                  <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Revenue impact of RMS rate changes</div>
                </div>
                <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{impact.kpis.rooms_analyzed}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Room-nights analyzed</div></div>
                <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{impact.kpis.decisions_analyzed}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Rate changes with bookings</div></div>
                <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}><span style={{ color: '#047857' }}>{impact.kpis.positive}</span> / <span style={{ color: '#b91c1c' }}>{impact.kpis.negative}</span></div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Positive vs negative</div></div>
                <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{impact.kpis.avg_delta_pct != null ? `${impact.kpis.avg_delta_pct > 0 ? '+' : ''}${impact.kpis.avg_delta_pct}%` : '—'}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Avg Δ (booking-weighted)</div></div>
              </div>

              {impact.market_context && (impact.market_context.revpar_this != null) && (
                <div style={{ ...banner('ok'), fontSize: 12 }}>
                  Market context (property-wide, not attributed to RMS): RevPAR {fmtINR(impact.market_context.revpar_this)} this period vs {fmtINR(impact.market_context.revpar_ly)} same period last year
                  {impact.market_context.lift_pct != null && <> ({impact.market_context.lift_pct > 0 ? '+' : ''}{impact.market_context.lift_pct}%)</>}.
                </div>
              )}
              <div style={{ ...banner('ok'), fontSize: 12 }}>{impact.note}</div>

              {impact.rows.length === 0 ? (
                <div style={{ padding: 24, background: 'var(--theme-surface-2, #f1f5f9)', borderRadius: 10, color: 'var(--theme-text-muted)', textAlign: 'center' }}>
                  No booked room-nights yet against an applied RMS rate change in this window. Apply some decisions, then check back once bookings come in.
                </div>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 10 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                    <thead><tr>{['Date', 'Room · Meal', 'Prior → New', 'Δ', 'Rooms booked since', 'Actual revenue', 'At prior rate', 'Impact'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {impact.rows.map((r, i) => {
                        const up = r.impact > 0;
                        return (
                          <tr key={i}>
                            <td style={td}>{r.stay_date}</td>
                            <td style={td}>{r.room_name} · {r.meal_plan_code}</td>
                            <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>
                              {r.prior_rate != null ? fmtINR(r.prior_rate) : '—'}{r.approx_prior && <span title="This slot was re-priced more than once before the stay date — the prior rate for this earliest change is approximate." style={{ color: '#d97706' }}> ≈</span>} → {fmtINR(r.new_rate)}
                            </td>
                            <td style={{ ...td, color: r.prior_rate && r.new_rate > r.prior_rate ? '#047857' : r.prior_rate && r.new_rate < r.prior_rate ? '#b91c1c' : '#64748b' }}>{r.prior_rate ? `${r.new_rate >= r.prior_rate ? '+' : ''}${(((r.new_rate - r.prior_rate) / r.prior_rate) * 100).toFixed(1)}%` : '—'}</td>
                            <td style={td}>{r.rooms_booked}</td>
                            <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{fmtINR(r.actual_revenue)}</td>
                            <td style={{ ...td, color: 'var(--theme-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{r.counterfactual_revenue != null ? fmtINR(r.counterfactual_revenue) : '—'}</td>
                            <td style={{ ...td, fontWeight: 700, color: r.impact == null ? undefined : up ? '#047857' : r.impact < 0 ? '#b91c1c' : '#64748b' }}>{r.impact != null ? `${up ? '+' : ''}${fmtINR(r.impact)}` : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Automation ── */}
      {tab === 'automation' && (
        <div style={{ display: 'grid', gap: 16, maxWidth: 680 }}>
          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Scope</div>
            <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginBottom: 10 }}>Set the mode + guardrails below for the whole property, or narrow them to one room type. A room left blank on any guardrail field inherits the property default for that field.</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={overrideRoom} onChange={(e) => setOverrideRoom(e.target.value)} style={{ ...sel, minWidth: 220 }} aria-label="Editing scope">
                <option value="">Property default (all rooms)</option>
                {rooms.map((r) => <option key={r.own_room_id} value={r.own_room_id}>{r.name}{roomOverrides.some((o) => String(o.room_type_id) === String(r.own_room_id)) ? ' (override set)' : ''}</option>)}
              </select>
              {overrideRoom && <span style={{ fontSize: 12, fontWeight: 700, color: config?.is_override || guard?.is_override ? '#7c3aed' : '#94a3b8' }}>{config?.is_override || guard?.is_override ? '● Has its own override' : '○ Currently inheriting property default'}</span>}
              {overrideRoom && (config?.is_override || guard?.is_override) && <button onClick={() => clearOverride(overrideRoom)} style={{ ...btn('ghost'), marginLeft: 'auto' }}>✕ Clear this room's override</button>}
            </div>
          </div>

          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Automation mode{overrideRoom ? ' — this room' : ''}</div>
            <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginBottom: 10 }}>How decisions move from recommendation to live rate. AUTOMATIC (auto-apply) ships in M2.</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['OFF', 'ADVISORY', 'APPROVAL'].map((m) => (
                <button key={m} onClick={() => saveConfig(m)} style={{ ...btn(mode === m ? 'primary' : 'ghost'), minWidth: 110 }}>{m}</button>
              ))}
              <button disabled title="Ships in M2" style={{ ...btn('ghost'), opacity: 0.5, cursor: 'not-allowed' }}>AUTOMATIC 🔒</button>
            </div>
            <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: mm.bg, color: mm.fg, fontSize: 13 }}>{mm.note}</div>
          </div>

          {guard && (
            <div style={card}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Guardrails{overrideRoom ? ' — this room' : ''}</div>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <label style={{ fontSize: 13, flex: 1 }}>Max daily change %<input type="number" value={guard.max_daily_change_pct ?? ''} onChange={(e) => setGuard({ ...guard, max_daily_change_pct: e.target.value })} placeholder={overrideRoom ? 'Inherit property default' : ''} style={{ ...sel, width: '100%', marginTop: 4 }} /></label>
                  <label style={{ fontSize: 13, flex: 1 }}>Max dates / run (blast radius)<input type="number" value={guard.max_dates_per_run ?? ''} onChange={(e) => setGuard({ ...guard, max_dates_per_run: e.target.value })} placeholder={overrideRoom ? 'Inherit property default' : ''} style={{ ...sel, width: '100%', marginTop: 4 }} /></label>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <label style={{ fontSize: 13, flex: 1 }}>Min rate ₹<input type="number" value={guard.min_rate ?? ''} onChange={(e) => setGuard({ ...guard, min_rate: e.target.value })} placeholder={overrideRoom ? 'Inherit property default' : 'No minimum'} style={{ ...sel, width: '100%', marginTop: 4 }} /></label>
                  <label style={{ fontSize: 13, flex: 1 }}>Max rate ₹<input type="number" value={guard.max_rate ?? ''} onChange={(e) => setGuard({ ...guard, max_rate: e.target.value })} placeholder={overrideRoom ? 'Inherit property default' : 'No maximum'} style={{ ...sel, width: '100%', marginTop: 4 }} /></label>
                </div>
                <label style={{ fontSize: 13 }}>Blackout dates (comma-separated YYYY-MM-DD){overrideRoom ? ' — adds to the property’s blackout dates for this room' : ''}<input type="text" value={guard.blackout_text} onChange={(e) => setGuard({ ...guard, blackout_text: e.target.value })} placeholder="2026-08-15, 2026-10-02" style={{ ...sel, width: '100%', marginTop: 4 }} /></label>
              </div>
              <div style={{ marginTop: 14 }}><button onClick={saveGuard} style={btn('primary')}>Save guardrails{overrideRoom ? ' for this room' : ''}</button></div>
            </div>
          )}

          {roomOverrides.length > 0 && (
            <div style={card}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Rooms with their own override</div>
              <div style={{ display: 'grid', gap: 8 }}>
                {roomOverrides.map((o) => (
                  <div key={o.room_type_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--theme-border-light, #f1f5f9)', borderRadius: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{o.room_name}</span>
                    {o.config?.mode && <span style={{ fontSize: 11, fontWeight: 700, background: '#ede9fe', color: '#6d28d9', borderRadius: 20, padding: '2px 8px' }}>{o.config.mode}</span>}
                    {o.guardrail && <span style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>
                      {o.guardrail.min_rate != null && `min ${fmtINR(o.guardrail.min_rate)} `}
                      {o.guardrail.max_rate != null && `max ${fmtINR(o.guardrail.max_rate)} `}
                      {o.guardrail.max_daily_change_pct != null && `±${o.guardrail.max_daily_change_pct}%/day`}
                    </span>}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <button onClick={() => setOverrideRoom(String(o.room_type_id))} style={{ ...btn('ghost'), padding: '4px 10px' }}>Edit</button>
                      <button onClick={() => clearOverride(o.room_type_id)} style={{ ...btn('reject'), padding: '4px 10px' }}>Clear</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Audit ── */}
      {tab === 'audit' && (
        <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 10 }}>
          {audit.length === 0 ? <div style={{ padding: 24, color: 'var(--theme-text-muted)', textAlign: 'center' }}>No pushes yet. Applying or rolling back a decision logs here.</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                <thead><tr>{['Time', 'Date', 'Room · Meal', 'Target', 'Action', 'Rate', 'Outcome'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {audit.map((l) => (
                    <tr key={l.id}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{new Date(l.pushed_at).toLocaleString()}</td>
                      <td style={td}>{l.stay_date || '—'}</td>
                      <td style={td}>{l.room_name ? `${l.room_name} · ${l.meal_plan_code}` : '—'}</td>
                      <td style={td}><span style={{ fontSize: 11, fontWeight: 700, background: l.target === 'CM' ? '#ecfeff' : '#f1f5f9', color: l.target === 'CM' ? '#155e75' : '#475569', borderRadius: 6, padding: '1px 7px' }}>{l.target}</span></td>
                      <td style={td}>{l.action}</td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{fmtINR(l.rate)}</td>
                      <td style={td}><span style={{ fontWeight: 700, fontSize: 12, color: l.outcome === 'SUCCESS' ? '#047857' : ['SKIPPED', 'NOT_CONFIGURED', 'NO_HOTEL_ID', 'NO_MAPPING', 'NO_RATE_DATA'].includes(l.outcome) ? '#92400e' : '#b91c1c' }}>{l.outcome}</span>{l.error && <span title={l.error} style={{ color: '#94a3b8', fontSize: 11 }}> ⓘ</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      )}

      {/* Decision detail drawer */}
      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={() => setDetail(null)}>
          <div role="dialog" aria-label="Decision detail" style={{ width: 'min(440px, 100%)', height: '100%', overflowY: 'auto', background: 'var(--theme-surface, #ffffff)', color: 'var(--theme-text, #374151)', padding: 18, boxShadow: '-4px 0 24px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div><h3 style={{ margin: 0, fontSize: 16 }}>{detail.room_name || detail.room_type_id} · {detail.meal_plan_code}</h3><div style={{ fontSize: 13, color: 'var(--theme-text-muted)' }}>{detail.stay_date} · <Chip status={detail.status} /></div></div>
              <button onClick={() => setDetail(null)} style={btn('ghost')} aria-label="Close">✕</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 14px', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 10, marginBottom: 12 }}>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Current</div><div style={{ fontSize: 18, fontWeight: 700 }}>{fmtINR(detail.prior_rate)}</div></div>
              <div style={{ fontSize: 20, color: '#94a3b8' }}>→</div>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Apply</div><div style={{ fontSize: 22, fontWeight: 800, color: '#7c3aed' }}>{fmtINR(detail.clamped_rate)}</div></div>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Δ</div><div style={{ fontSize: 16, fontWeight: 700 }}>{detail.delta_pct != null ? `${Number(detail.delta_pct) > 0 ? '+' : ''}${Number(detail.delta_pct).toFixed(1)}%` : '—'}</div></div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>
              <div style={{ fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Trace</div>
              <div>Recommendation (proposed): {fmtINR(detail.proposed_rate)}</div>
              <div>Mode at ingest: {detail.mode}</div>
              {detail.guardrail_trace_json?.guardrail && <div>Guardrail: max daily ±{detail.guardrail_trace_json.guardrail.max_daily_change_pct}%{detail.guardrail_trace_json.guardrail.bound ? ` · clamped by ${detail.guardrail_trace_json.guardrail.bound.replace(/_/g, ' ').toLowerCase()}` : ''}</div>}
              {detail.applied_at && <div>Applied {new Date(detail.applied_at).toLocaleString()}{detail.acted_by ? ` by ${detail.acted_by}` : ''}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              {(detail.status === 'PROPOSED' || detail.status === 'APPROVED') && <button onClick={() => act(detail.id, 'apply', `Apply ${fmtINR(detail.clamped_rate)} for ${detail.stay_date}? Writes live PMS + CM rate.`)} style={{ ...btn('apply'), flex: 1 }}>▶ Apply</button>}
              {(detail.status === 'PROPOSED' || detail.status === 'HELD') && <button onClick={() => act(detail.id, 'approve')} style={{ ...btn('ghost'), flex: 1 }}>Approve</button>}
              {(detail.status === 'PROPOSED' || detail.status === 'APPROVED' || detail.status === 'HELD') && <button onClick={() => act(detail.id, 'reject')} style={{ ...btn('reject'), flex: 1 }}>Reject</button>}
              {detail.status === 'APPLIED' && <button onClick={() => act(detail.id, 'rollback', `Roll back to ${fmtINR(detail.prior_rate)} for ${detail.stay_date}?`)} style={{ ...btn('ghost'), flex: 1 }}>↩ Rollback</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RevenueManagerRms;
